import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import axios from 'axios'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {UserModel} from './_models'
import {getUserByToken, logOutUser} from './_requests'
import {WithChildren} from '../../../../_metronic/helpers'
import {TFranchiseResponse} from '../../../../coreGlobal/models/franchise/franchise'
import {getUserFranchise} from '../../../../pages/chooseFranchise/core/_request'
import {useLocation, useNavigate} from 'react-router-dom'
import {echoReverb} from './reverbNotification'

type AuthContextProps = {
  currentFranchise: TFranchiseResponse | undefined
  setCurrentFranchise: Dispatch<SetStateAction<TFranchiseResponse | undefined>>
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
}

const initAuthContextPropsState = {
  currentUser: undefined,
  setCurrentUser: () => {},
  currentFranchise: undefined,
  setCurrentFranchise: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const [currentFranchise, setCurrentFranchise] = useState<TFranchiseResponse | undefined>()

  const logout = async () => {
    try {
      await logOutUser()
      setCurrentUser(undefined)
    } catch (e) {}
  }

  return (
    <AuthContext.Provider
      value={{currentUser, setCurrentUser, currentFranchise, setCurrentFranchise, logout}}
    >
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {logout, setCurrentUser, setCurrentFranchise, currentUser} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  const navigate = useNavigate()

  const location = useLocation()

  useEffect(() => {
    const requestUser = async () => {
      try {
        if (!didRequest.current) {
          // Garante que o cookie CSRF foi criado antes de qualquer requisição protegida
          await axios.get(`${process.env.REACT_APP_API_URL}/sanctum/csrf-cookie`)

          const userByToken = await getUserByToken()
          try {
            echoReverb(userByToken.id, setCurrentUser)
          } catch (e) {
            // console.log(e)
          }
          const userByTokenFilter: any = userByToken
            ? {
                ...userByToken,
                unreadNotifications: userByToken.unreadNotifications
                  ? userByToken.unreadNotifications.map((notification: any) => {
                      return {
                        ...notification.data,
                        id: notification.id,
                        type: notification.type,
                      }
                    })
                  : [],
              }
            : ({} as UserModel)

          setCurrentUser(userByTokenFilter)

          const franchise = await getUserFranchise()
          setCurrentFranchise(franchise)
        }
      } catch (error: any) {
        if (!didRequest.current && currentUser) {
          logout()
        }
      } finally {
        setShowSplashScreen(false)
      }
      return () => (didRequest.current = true)
    }

    requestUser()
  }, [])

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Garante que existe uma resposta
        if (!error.response) {
          return Promise.reject(error)
        }

        const status = error.response.status

        // Telas que não devem disparar logout
        const ignoredPaths = [
          '/auth/forgot-password',
          '/password-reset',
          '/payment',
          '/p/',
          '/recurrence/change-credit-card',
          '/budget',
        ]
        const isIgnoredPath = ignoredPaths.some((path) => location.pathname.startsWith(path))

        // Logout em 401 (exceto urls ignoradas)
        if (status === 401 && !error.config.url.includes('logout') && !isIgnoredPath) {
          logout()
          navigate('/auth/login', {replace: true})
          error.handled = true
          return Promise.reject(error)
        }

        // Trata o erro 403 (sem permissão)
        if (status === 403) {
          const message = error.response.data?.error
          if (
            message === 'Not selected franchise.' ||
            message === 'Acesso restrito por vínculo de franquia.'
          ) {
            navigate('/auth/franquias', {replace: true})
          } else if (message === 'Change your password.') {
            navigate('/auth/resetpassword', {replace: true})
          }
        }

        // Sempre rejeita o erro no final para manter a cadeia do Axios funcional
        return Promise.reject(error)
      }
    )

    return () => {
      axios.interceptors.response.eject(interceptor)
    }
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
